/**
 * Format API error responses for display
 * Handles various error formats including Pydantic validation errors
 *
 * @param {Error} err - The error object from axios
 * @param {string} fallbackMessage - Default message if error can't be parsed
 * @returns {string} Formatted error message
 */
export const formatErrorMessage = (err, fallbackMessage = 'An error occurred') => {
  console.error('Error details:', err);

  // Check for response data
  if (err.response?.data?.detail) {
    const detail = err.response.data.detail;

    // Handle Pydantic validation errors (array of error objects)
    if (Array.isArray(detail)) {
      const errors = detail.map(e => {
        // e.loc is typically ['body', 'field_name'] or ['field_name']
        const field = e.loc?.slice(1).join('.') || e.loc?.join('.') || 'Field';
        const message = e.msg || 'Invalid value';
        return `${field}: ${message}`;
      });

      // Return first 3 errors to avoid overwhelming the user
      if (errors.length > 3) {
        return errors.slice(0, 3).join('; ') + ` (and ${errors.length - 3} more)`;
      }
      return errors.join('; ');
    }

    // Handle string detail
    if (typeof detail === 'string') {
      return detail;
    }

    // Handle object detail (try to extract message)
    if (typeof detail === 'object') {
      return detail.message || JSON.stringify(detail);
    }
  }

  // Check for alternative message formats
  if (err.response?.data?.message) {
    return err.response.data.message;
  }

  // Check for error message property
  if (err.message) {
    return err.message;
  }

  // Return fallback
  return fallbackMessage;
};

/**
 * Format validation errors for display in forms
 * Returns an object mapping field names to error messages
 *
 * @param {Error} err - The error object from axios
 * @returns {Object} Object mapping field names to error messages
 */
export const formatValidationErrors = (err) => {
  const errors = {};

  if (err.response?.data?.detail && Array.isArray(err.response.data.detail)) {
    err.response.data.detail.forEach(e => {
      // Extract field name (skip 'body' prefix if present)
      const fieldPath = e.loc?.slice(1) || e.loc || [];
      const field = fieldPath.join('.');

      if (field) {
        errors[field] = e.msg || 'Invalid value';
      }
    });
  }

  return errors;
};

/**
 * Check if error is a validation error
 * @param {Error} err - The error object
 * @returns {boolean}
 */
export const isValidationError = (err) => {
  return err.response?.status === 422 ||
         (Array.isArray(err.response?.data?.detail) &&
          err.response?.data?.detail[0]?.type);
};

/**
 * Check if error is an authentication error
 * @param {Error} err - The error object
 * @returns {boolean}
 */
export const isAuthError = (err) => {
  return err.response?.status === 401 || err.response?.status === 403;
};
